import React, { useState, useEffect } from 'react';
// Import the JSON-based configuration
import appConfig, { ENDPOINT_KEYS } from './config/appConfig';


const EditConsumerDialog = ({
  isOpen,
  onClose,
  selectedRequirements,
  consumer,
  corporateAccount,
  currentProject,
  login_token
}) => {
  const [consumerDescription, setConsumerDescription] = useState('');
  const [integrationType, setIntegrationType] = useState('');
  const [targetDataFormat, setTargetDataFormat] = useState('');
  const [status, setStatus] = useState('');
  const [statuses, setStatuses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Use the same dialog styles as ManageConsumersDialog
  const DialogStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '0',
      margin: '0'
    },
    dialog: {
      backgroundColor: 'white',
      padding: '0px 20px 10px 20px',
      borderRadius: '8px',
      width: '700px',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      flexDirection: 'column',
      gap: '0'
    },
    title: {
      fontSize: '18px',
      textAlign: 'center',
      margin: 0,
      padding: '10px 0',
      color: '#000'
    }
  };

  // Initialize form with consumer data when dialog opens or consumer changes
  useEffect(() => {
    if (isOpen && consumer) {
      setConsumerDescription(consumer.consumer_description || '');
      setIntegrationType(consumer.integration_type || '');
      setTargetDataFormat(consumer.target_data_format || '');
      setStatus(consumer.status || '');
      fetchStatuses();
    }
  }, [isOpen, consumer]);

  const fetchStatuses = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(appConfig.getUrl('/get_statuses'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          entity: 'REQUIREMENT',
          search_query: ''
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        const statusOptions = data.entity_statuses.map(s => s.status);
        setStatuses(statusOptions);
      } else {
        throw new Error(data.status_description || 'Failed to fetch statuses');
      }
    } catch (err) {
      console.error('Error fetching statuses:', err);
      setError('Failed to load status options');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!consumer || !consumer.target_or_consumer_system_id) {
      setError('Invalid consumer data');
      return;
    }

    if (selectedRequirements.size !== 1) {
      setError('Please select exactly one integration requirement');
      return;
    }

    setIsLoading(true);
    setError('');

    const integrationId = Array.from(selectedRequirements)[0];

    try {
      const response = await fetch(appConfig.getUrl('/update_integration_requirement_consumer'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          integration_id: integrationId,
          target_or_consumer_system_id: consumer.target_or_consumer_system_id,
          consumer_description: consumerDescription,
          integration_type: integrationType,
          target_data_format: targetDataFormat,
          status: status
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        onClose(true);
      } else {
        throw new Error(data.status_description || 'Failed to update consumer');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error updating consumer:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    onClose(false);
  };

  if (!isOpen || !consumer) return null;

  return (
    <div style={DialogStyles.overlay}>
      <div style={DialogStyles.dialog}>
        <h2 style={{ ...DialogStyles.title, backgroundColor: '#CCCCFF', height: 40 }}>Edit Consumer</h2>

        <div className="mb-6 p-4 border border-gray-300 rounded-md">
          <h3 className="text-base font-semibold mb-3">System Information</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">System Name:</label>
              <div className="p-2 bg-gray-100 rounded text-gray-800">{consumer.system_name}</div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">System Acronym:</label>
              <div className="p-2 bg-gray-100 rounded text-gray-800">{consumer.system_acronym}</div>
            </div>
          </div>
          <div className="mt-3">
            <label className="block text-sm font-medium mb-1">System Description:</label>
            <div className="p-2 bg-gray-100 rounded text-gray-800 max-h-24 overflow-y-auto">
              {consumer.system_description}
            </div>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">
            Consumer Description:
          </label>
          <textarea
            value={consumerDescription}
            onChange={(e) => setConsumerDescription(e.target.value)}
            className="w-full p-2 border rounded"
            rows={3}
            placeholder="Describe how this system consumes the integration"
            disabled={selectedRequirements.size !== 1}
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">
            Integration Type:
          </label>
          <select
            value={integrationType}
            onChange={(e) => setIntegrationType(e.target.value)}
            className="w-full p-2 border rounded"
            disabled={selectedRequirements.size !== 1}
          >
            <option value="">Select integration type</option>
            <option value="Sync">Synchronous</option>
            <option value="Async">Asynchronous</option>
          </select>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">
            Target Data Format:
          </label>
          <select
            value={targetDataFormat}
            onChange={(e) => setTargetDataFormat(e.target.value)}
            className="w-full p-2 border rounded"
            disabled={selectedRequirements.size !== 1}
          >
            <option value="">Select data format</option>
            <option value="JSON">JSON</option>
            <option value="XML">XML</option>
            <option value="CSV">CSV</option>
            <option value="Binary">Binary</option>
            <option value="EDI">EDI</option>
            <option value="Flat File">Flat File</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">
            Status:
          </label>
          {isLoading ? (
            <div className="w-full p-2 bg-gray-100 rounded text-gray-500">Loading statuses...</div>
          ) : (
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full p-2 border rounded"
              disabled={selectedRequirements.size !== 1}
            >
              <option value="">Select status</option>
              {statuses.map(statusOption => (
                <option key={statusOption} value={statusOption}>
                  {statusOption}
                </option>
              ))}
            </select>
          )}
        </div>

        {error && (
          <div className="mb-4 p-2 rounded bg-red-50 text-red-500">
            {error}
          </div>
        )}

        <div className="flex justify-center gap-4 mt-6">
          <button
            onClick={handleCancel}
            style={{
              backgroundColor: '#FFFDD0',
              padding: '8px 16px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
            disabled={isLoading}
          >
            Close
          </button>
          <button
            onClick={handleSubmit}
            style={{
              padding: '8px 16px',
              backgroundColor: '#C8F4DE',
              color: 'black',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
            disabled={isLoading || selectedRequirements.size !== 1}
          >
            {isLoading ? 'Updating...' : 'Update Consumer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditConsumerDialog;